import { useEffect, useState } from 'react';
import DoctorEarnings from '../components/DoctorEarnings';
import { safeJson, networkMessage } from '../apiHelpers';
import PiPrice from '../components/PiPrice';
import { t } from '../i18n';
import ReviewBox, { VerifiedBadge } from '../components/ReviewBox';

const API = '/api/health';
function authHeaders() {
  const token = localStorage.getItem('token');
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Token ${token}` } : {}) };
}
const C = { bg: '#FCFCFA', surface: '#fff', border: '#E3E1D9', text: '#2C2C2A', muted: '#5F5E5A', primary: '#0F6E56' };
const inp = { width: '100%', padding: '9px 11px', fontSize: 14, border: `0.5px solid ${C.border}`, borderRadius: 8, boxSizing: 'border-box', marginBottom: 8 };
const lbl = { fontSize: 12, color: C.muted, display: 'block', marginBottom: 3 };
const btn = { padding: '8px 14px', fontSize: 13, fontWeight: 600, border: 'none', borderRadius: 8, background: C.primary, color: '#fff', cursor: 'pointer' };
const btnGhost = { ...btn, background: '#fff', color: C.text, border: `0.5px solid ${C.border}` };
const tabBtn = (a) => ({ padding: '8px 12px', fontSize: 12.5, fontWeight: 500, border: 'none', borderBottom: a ? `2px solid ${C.primary}` : '2px solid transparent', background: 'transparent', color: a ? C.primary : C.muted, cursor: 'pointer' });

const DEPTS = { general: 'General Physician', child: 'Child Specialist', skin: 'Dermatologist', gyne: 'Gynecologist', heart: 'Cardiologist', ent: 'ENT', ortho: 'Orthopedic', psych: 'Psychiatrist', dental: 'Dentist', eye: 'Eye Specialist', other: 'Other' };
const DEPT_ICON = { general: '🩺', child: '👶', skin: '🧴', gyne: '🤰', heart: '❤️', ent: '👂', ortho: '🦴', psych: '🧠', dental: '🦷', eye: '👁️', other: '🏥' };

export default function Health() {
  const [tab, setTab] = useState('find');
  const [facType, setFacType] = useState('lab');
  useEffect(() => {
    const h = (e) => { const p = String(e.detail).split(':'); setTab(p[0]); if (p[1]) setFacType(p[1]); };
    window.addEventListener('health-tab', h);
    return () => window.removeEventListener('health-tab', h);
  }, []);
  return (
    <div style={{ maxWidth: 820, margin: '0 auto', padding: '1.5rem', fontFamily: 'sans-serif', background: C.bg, minHeight: '100vh' }}>
      <h1 style={{ fontSize: 22, margin: '0 0 4px' }}>🩺 {t('hl_title')}</h1>
      <p style={{ fontSize: 13, color: C.muted, margin: '0 0 10px' }}>{t('hl_sub')}</p>
      <div style={{ display: 'flex', gap: 2, borderBottom: `0.5px solid ${C.border}`, marginBottom: 14, flexWrap: 'wrap' }}>
        <button style={tabBtn(tab === 'find')} onClick={() => setTab('find')}>{t('hl_find_doctor')}</button>
        <button style={tabBtn(tab === 'plans')} onClick={() => setTab('plans')}>{t('hl_plans')}</button>
        <button style={tabBtn(tab === 'appts')} onClick={() => setTab('appts')}>{t('hl_my_appointments')}</button>
        <button style={tabBtn(tab === 'facilities')} onClick={() => setTab('facilities')}>{t('hf_tab')}</button>
        <button style={tabBtn(tab === 'doctor')} onClick={() => setTab('doctor')}>{t('hl_become_doctor')}</button>
        <button style={tabBtn(tab === 'reports')} onClick={() => setTab('reports')}>{t('hl_reports_tab')}</button>
        <button style={tabBtn(tab === 'cart')} onClick={() => setTab('cart')}>🛒 {t('hl_cart_tab')}</button>
        <button style={tabBtn(tab === 'tips')} onClick={() => setTab('tips')}>🎥 {t('hl_tips_tab')}</button>
        <button style={tabBtn(tab === 'earnings')} onClick={() => setTab('earnings')}>💰 My Earnings</button>
      </div>
      {tab === 'find' && <FindDoctor />}
      {tab === 'plans' && <Plans />}
      {tab === 'appts' && <MyAppointments />}
      {tab === 'facilities' && <Facilities type={facType} setType={setFacType} />}
      {tab === 'doctor' && <BecomeDoctor />}
      {tab === 'reports' && <MyReports />}
      {tab === 'cart' && <HealthCart />}
      {tab === 'tips' && <DoctorTips />}
      {tab === 'earnings' && <DoctorEarnings />}
      <p style={{ fontSize: 11, color: C.muted, marginTop: 20, fontStyle: 'italic', borderTop: `0.5px solid ${C.border}`, paddingTop: 10 }}>⚠️ {t('hl_disclaimer')}</p>
    </div>
  );
}

function FindDoctor() {
  const [docs, setDocs] = useState(null);
  const [dept, setDept] = useState('');
  const [city, setCity] = useState('');
  const [booking, setBooking] = useState(null);
  const load = () => fetch(`${API}/doctors/?department=${dept}&city=${encodeURIComponent(city)}`).then((r) => r.json()).then((d) => setDocs(d.doctors || []));
  useEffect(() => { load(); }, []); // eslint-disable-line
  const pickDept = (k) => {
    setDept(k);
    fetch(`${API}/doctors/?department=${k}&city=${encodeURIComponent(city)}`).then((r) => r.json()).then((d) => setDocs(d.doctors || []));
  };

  if (booking) return <BookAppt doctor={booking} onBack={() => setBooking(null)} />;
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 18 }}>
        {[
          ['🎥', t('hs_consult_online'), t('hs_consult_sub'), '#0D9488', null],
          ['💳', t('hs_plans_card'), t('hs_plans_sub'), '#185FA5', 'plans'],
          ['📋', t('hs_appts_card'), t('hs_appts_sub'), '#0F6E56', 'appts'],
          ['🩸', t('hs_blood_card'), t('hs_blood_sub'), '#C0392B', 'blood'],
        ].map(([icon, title, sub, color, target]) => (
          <div key={title} onClick={() => { if (target === 'blood') window.location.href = '/blood-bank'; else if (target) window.dispatchEvent(new CustomEvent('health-tab', { detail: target })); }}
            style={{ background: C.surface, border: `0.5px solid ${C.border}`, borderTop: `3px solid ${color}`, borderRadius: 10, padding: '0.8rem 0.7rem', cursor: 'pointer' }}>
            <div style={{ fontSize: 22, marginBottom: 4 }}>{icon}</div>
            <p style={{ fontSize: 13, fontWeight: 700, margin: '0 0 2px', color: C.text }}>{title}</p>
            <p style={{ fontSize: 11, color: C.muted, margin: 0, lineHeight: 1.3 }}>{sub}</p>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 18 }}>
        {[
          ['🧪', t('hf_labs'), t('hf_labs_sub'), '#1d4ed8', 'lab'],
          ['💊', t('hf_pharmacy'), t('hf_pharmacy_sub'), '#0891B2', 'pharmacy'],
          ['🏥', t('hf_hospitals'), t('hf_hospitals_sub'), '#0F6E56', 'hospital'],
        ].map(([icon, title, sub, color, ftype]) => (
          <div key={title} onClick={() => window.dispatchEvent(new CustomEvent('health-tab', { detail: 'facilities:' + ftype }))}
            style={{ background: C.surface, border: `0.5px solid ${C.border}`, borderTop: `3px solid ${color}`, borderRadius: 10, padding: '0.8rem 0.7rem', cursor: 'pointer' }}>
            <div style={{ fontSize: 22, marginBottom: 4 }}>{icon}</div>
            <p style={{ fontSize: 13, fontWeight: 700, margin: '0 0 2px', color: C.text }}>{title}</p>
            <p style={{ fontSize: 11, color: C.muted, margin: 0, lineHeight: 1.3 }}>{sub}</p>
          </div>
        ))}
      </div>

      <p style={{ fontSize: 14, fontWeight: 700, margin: '0 0 8px' }}>{t('hs_specialties')}</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(84px, 1fr))', gap: 8, marginBottom: 18 }}>
        {Object.entries(DEPTS).map(([k, v]) => (
          <div key={k} onClick={() => pickDept(k)}
            style={{ textAlign: 'center', cursor: 'pointer', padding: '0.6rem 0.3rem', borderRadius: 10, background: dept === k ? '#E1F5EE' : C.surface, border: `0.5px solid ${dept === k ? C.primary : C.border}` }}>
            <div style={{ fontSize: 24, marginBottom: 3 }}>{DEPT_ICON[k] || '🏥'}</div>
            <p style={{ fontSize: 11, color: dept === k ? C.primary : C.text, margin: 0, lineHeight: 1.25, fontWeight: dept === k ? 600 : 400 }}>{v}</p>
          </div>
        ))}
      </div>
      {dept && <button style={{ ...btnGhost, fontSize: 12, padding: '5px 12px', marginBottom: 10 }} onClick={() => pickDept('')}>✕ {t('hs_all_doctors')}</button>}

      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        <select style={{ ...inp, marginBottom: 0, flex: 1 }} value={dept} onChange={(e) => setDept(e.target.value)}>
          <option value="">{t('hl_department')}</option>
          {Object.entries(DEPTS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <input style={{ ...inp, marginBottom: 0, flex: 1 }} placeholder={t('hl_city')} value={city} onChange={(e) => setCity(e.target.value)} />
        <button style={btnGhost} onClick={load}>{t('search')}</button>
      </div>
      {docs === null && <p style={{ fontSize: 13, color: C.muted }}>{t('loading')}</p>}
      {docs && docs.length === 0 && <p style={{ fontSize: 13, color: C.muted }}>{t('hl_no_doctors')}</p>}
      {docs && docs.map((d) => (
        <div key={d.id} style={{ background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 12, padding: '1rem', marginBottom: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 15, fontWeight: 600 }}>Dr. {d.name}</span>
                <VerifiedBadge module="doctor" objectId={d.id} />
              </div>
              <p style={{ fontSize: 12, color: C.primary, margin: '2px 0' }}>{DEPTS[d.department] || d.department}</p>
              {d.qualifications && <p style={{ fontSize: 12, color: C.muted, margin: '2px 0' }}>{d.qualifications}</p>}
              {d.clinic && <p style={{ fontSize: 12, color: C.muted, margin: '2px 0' }}>🏥 {d.clinic}{d.city ? `, ${d.city}` : ''}</p>}
              {d.availability && <p style={{ fontSize: 12, color: C.muted, margin: '2px 0' }}>🕐 {d.availability}</p>}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <button style={btn} onClick={() => setBooking(d)}>{t('hl_book')}</button>
              {d.username && <a href={`/chat/${d.username}`} style={{ ...btnGhost, textDecoration: 'none', textAlign: 'center', fontSize: 12 }}>💬 {t('hl_chat')}</a>}
            </div>
          </div>
          {d.bio && <p style={{ fontSize: 13, margin: '8px 0 0' }}>{d.bio}</p>}
          <ReviewBox module="doctor" objectId={d.id} objectTitle={d.name} />
        </div>
      ))}
    </div>
  );
}

function BookAppt({ doctor, onBack }) {
  const [form, setForm] = useState({ scheduled_datetime: '', reason: '' });
  const [sub, setSub] = useState(null);
  const [msg, setMsg] = useState('');
  const [myReports, setMyReports] = useState([]);
  const [linkedIds, setLinkedIds] = useState([]);
  useEffect(() => { fetch(`${API}/subscription/`, { headers: authHeaders() }).then((r) => r.json()).then(setSub); }, []);
  useEffect(() => { fetch(`${API}/reports/mine/`, { headers: authHeaders() }).then((r) => r.json()).then((d) => setMyReports(d.reports || [])); }, []);
  const toggleReport = (id) => setLinkedIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));

  const book = async () => {
    if (!form.scheduled_datetime) { setMsg(t('hl_date_time') + ' ?'); return; }
    const res = await fetch(`${API}/appointments/book/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ doctor_id: doctor.id, ...form, linked_report_ids: linkedIds }) });
    const d = await safeJson(res);
    if (d.success) { alert('✅ Booked! Check "My Appointments" for the video link.'); onBack(); }
    else setMsg(d.error || 'Failed');
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <div style={{ background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 12, padding: '1.2rem' }}>
      <button onClick={onBack} style={{ ...btnGhost, marginBottom: 12 }}>← Dr. {doctor.name}</button>
      {sub && !sub.active && <p style={{ fontSize: 12, color: '#C0392B', margin: '0 0 8px' }}>{t('hl_need_sub')}</p>}
      <label style={lbl}>{t('hl_reason')}</label>
      <textarea style={{ ...inp, minHeight: 60 }} value={form.reason} onChange={set('reason')} />
      <label style={lbl}>{t('hl_date_time')}</label>
      <input type="datetime-local" style={inp} value={form.scheduled_datetime} onChange={set('scheduled_datetime')} />
      {myReports.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <label style={lbl}>{t('hl_link_reports')}</label>
          {myReports.map((r) => (
            <label key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, padding: '4px 0', cursor: 'pointer' }}>
              <input type="checkbox" checked={linkedIds.includes(r.id)} onChange={() => toggleReport(r.id)} />
              {r.title} <span style={{ color: C.muted }}>· {r.date}</span>
            </label>
          ))}
        </div>
      )}
      {msg && <p style={{ fontSize: 13, color: '#A32D2D' }}>{msg}</p>}
      <button style={btn} onClick={book}>{t('hl_confirm')}</button>
    </div>
  );
}

function Plans() {
  const [plans, setPlans] = useState(null);
  const [sub, setSub] = useState(null);
  const [msg, setMsg] = useState('');
  useEffect(() => {
    fetch(`${API}/plans/`).then((r) => r.json()).then((d) => setPlans(d.plans || []));
    if (localStorage.getItem('token')) fetch(`${API}/subscription/`, { headers: authHeaders() }).then((r) => r.json()).then(setSub);
  }, []);

  const subscribe = async (plan) => {
    if (!window.Pi) { setMsg('Pi Browser mein kholein'); return; }
    if (window.xpertPayConfirm && !(await window.xpertPayConfirm({ amount: plan.price_pi, memo: 'Health plan: ' + plan.name }))) return;
    await window.Pi.authenticate(['username', 'payments'], () => {});
    window.Pi.createPayment(
      { amount: plan.price_pi, memo: 'Health subscription', metadata: { type: 'health_plan', plan_id: plan.id } },
      {
        onReadyForServerApproval: (paymentId) => {
          fetch('/api/payments/approve/', {
            method: 'POST', headers: authHeaders(),
            body: JSON.stringify({ paymentId, amount: plan.price_pi }),
          });
        },
        onReadyForServerCompletion: (paymentId, txid) => {
          fetch('/api/payments/complete/', {
            method: 'POST', headers: authHeaders(),
            body: JSON.stringify({ paymentId, txid }),
          })
            .then(() => fetch(`${API}/subscribe/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ plan_id: plan.id, payment_id: paymentId, txid }) }))
            .then(() => { alert('✅ Subscribed!'); window.location.reload(); })
            .catch(() => setMsg('Payment completed but subscription failed - contact support with your transaction ID.'));
        },
        onCancel: () => setMsg('Cancelled'), onError: () => setMsg('Payment error'),
      }
    );
  };

  const addToCart = async (plan) => {
    const res = await fetch('/api/cart/add/', {
      method: 'POST', headers: authHeaders(),
      body: JSON.stringify({
        app_name: 'health', item_type: 'plan', reference_id: String(plan.id),
        title: plan.name, price_pi: plan.price_pi,
        metadata: { duration_days: plan.duration_days, checkups_included: plan.checkups_included },
      }),
    });
    if (res.ok) { setMsg('✅ Added to cart - go to 🛒 Cart tab to pay.'); } else { setMsg('Could not add to cart.'); }
  };

  if (plans === null) return <p style={{ fontSize: 13, color: C.muted }}>{t('loading')}</p>;
  return (
    <div>
      {sub && sub.active && <div style={{ background: '#E1F5EE', borderRadius: 10, padding: '0.7rem 1rem', marginBottom: 12, fontSize: 13, color: C.primary }}>✅ {t('hl_subscribed')}: {sub.plan} · {sub.checkups_included === 0 ? '∞' : `${sub.checkups_used}/${sub.checkups_included}`} checkups</div>}
      {plans.length === 0 && <p style={{ fontSize: 13, color: C.muted }}>No plans available yet.</p>}
      {plans.map((p) => (
        <div key={p.id} style={{ background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 12, padding: '1rem', marginBottom: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <p style={{ fontSize: 15, fontWeight: 600, margin: '0 0 2px' }}>{p.name}</p>
              <p style={{ fontSize: 12, color: C.muted, margin: 0 }}>{p.duration_days} days · {p.checkups === 0 ? 'Unlimited' : p.checkups} checkups{p.description ? ` · ${p.description}` : ''}</p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 17, fontWeight: 700, color: C.primary }}><PiPrice pi={p.price_pi} /></div>
              <button style={{ ...btn, marginTop: 4 }} onClick={() => addToCart(p)}>🛒 {t('hl_add_to_cart')}</button>
            </div>
          </div>
        </div>
      ))}
      {msg && <p style={{ fontSize: 13, color: '#A32D2D' }}>{msg}</p>}
    </div>
  );
}

function ApptDetail({ appt, onClose }) {
  const [data, setData] = useState(null);
  const [meds, setMeds] = useState([{ name: '', dose: '' }]);
  const [perhaiz, setPerhaiz] = useState('');
  const [note, setNote] = useState('');
  const [reportTitle, setReportTitle] = useState('');
  const [file, setFile] = useState(null);
  const [msg, setMsg] = useState('');

  const load = () => fetch(`${API}/appointments/${appt.id}/detail/`, { headers: authHeaders() }).then((r) => r.json()).then((d) => {
    setData(d);
    if (d.prescription && d.prescription.medicines && d.prescription.medicines.length) {
      setMeds(d.prescription.medicines); setPerhaiz(d.prescription.perhaiz || ''); setNote(d.prescription.note || '');
    }
  });
  useEffect(() => { load(); }, []); // eslint-disable-line

  const uploadReport = async () => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file); fd.append('title', reportTitle);
    const token = localStorage.getItem('token');
    const res = await fetch(`${API}/appointments/${appt.id}/upload-report/`, { method: 'POST', headers: token ? { Authorization: `Token ${token}` } : {}, body: fd });
    const d = await safeJson(res);
    setMsg(d.success ? '✅' : (d.error || 'Failed'));
    if (d.success) { setFile(null); setReportTitle(''); load(); }
  };

  const savePresc = async () => {
    const clean = meds.filter((m) => m.name.trim());
    const res = await fetch(`${API}/appointments/${appt.id}/prescription/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ medicines: clean, perhaiz, note }) });
    const d = await safeJson(res);
    setMsg(d.success ? '✅' : (d.error || 'Failed'));
    if (d.success) load();
  };

  if (!data) return <p style={{ fontSize: 12, color: C.muted }}>{t('loading')}</p>;
  const isDoctor = appt.role === 'doctor';

  return (
    <div style={{ borderTop: `0.5px solid ${C.border}`, marginTop: 8, paddingTop: 8 }}>
      <p style={{ fontSize: 12, fontWeight: 600, margin: '0 0 4px' }}>📎 {t('hp_reports')}</p>
      {data.reports.length === 0 && <p style={{ fontSize: 12, color: C.muted, margin: '0 0 6px' }}>{t('hp_no_reports')}</p>}
      {data.reports.map((r) => (
        <a key={r.id} href={`${window.location.origin}${r.url}`} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: C.primary, display: 'block' }}>📄 {r.title} ({r.date}) — {t('hp_view_report')}</a>
      ))}
      {!isDoctor && (
        <div style={{ marginTop: 6, marginBottom: 10 }}>
          <input style={{ ...inp, marginBottom: 4 }} placeholder={t('hp_report_title')} value={reportTitle} onChange={(e) => setReportTitle(e.target.value)} />
          <input type="file" accept="image/*,application/pdf" onChange={(e) => setFile(e.target.files[0])} style={{ fontSize: 12, marginBottom: 4 }} />
          <button style={{ ...btn, fontSize: 12, padding: '5px 12px' }} onClick={uploadReport}>{t('hp_upload_report')}</button>
        </div>
      )}

      <p style={{ fontSize: 12, fontWeight: 600, margin: '10px 0 4px' }}>💊 {t('hp_prescription')}</p>
      {!isDoctor && (
        !data.prescription ? <p style={{ fontSize: 12, color: C.muted }}>{t('hp_no_presc')}</p> : (
          <div style={{ background: '#F7F5F0', borderRadius: 8, padding: 8, fontSize: 13 }}>
            {(data.prescription.medicines || []).map((m, i) => <div key={i}>💊 <b>{m.name}</b>{m.dose ? ` — ${m.dose}` : ''}</div>)}
            {data.prescription.perhaiz && <p style={{ margin: '6px 0 0' }}><b>{t('hp_perhaiz')}:</b> {data.prescription.perhaiz}</p>}
            {data.prescription.note && <p style={{ margin: '4px 0 0', color: C.muted }}>{data.prescription.note}</p>}
          </div>
        )
      )}
      {isDoctor && (
        <div>
          {meds.map((m, i) => (
            <div key={i} style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
              <input style={{ ...inp, marginBottom: 0, flex: 2 }} placeholder={t('hp_medicine')} value={m.name} onChange={(e) => { const c = meds.slice(); c[i] = { ...c[i], name: e.target.value }; setMeds(c); }} />
              <input style={{ ...inp, marginBottom: 0, flex: 1 }} placeholder={t('hp_dose')} value={m.dose} onChange={(e) => { const c = meds.slice(); c[i] = { ...c[i], dose: e.target.value }; setMeds(c); }} />
            </div>
          ))}
          <button style={{ ...btnGhost, fontSize: 11, padding: '3px 10px', marginBottom: 6 }} onClick={() => setMeds([...meds, { name: '', dose: '' }])}>+ {t('hp_add_medicine')}</button>
          <textarea style={{ ...inp, minHeight: 40 }} placeholder={t('hp_perhaiz')} value={perhaiz} onChange={(e) => setPerhaiz(e.target.value)} />
          <textarea style={{ ...inp, minHeight: 40 }} placeholder={t('hp_presc_note')} value={note} onChange={(e) => setNote(e.target.value)} />
          <button style={{ ...btn, fontSize: 12, padding: '6px 14px' }} onClick={savePresc}>{t('hp_save_presc')}</button>
        </div>
      )}
      {msg && <span style={{ fontSize: 12, color: C.primary, marginLeft: 8 }}>{msg}</span>}
      <button style={{ ...btnGhost, fontSize: 11, padding: '3px 10px', marginTop: 8, display: 'block' }} onClick={onClose}>✕ {t('rm_cancel') || 'Close'}</button>
    </div>
  );
}

function MyAppointments() {
  const [appts, setAppts] = useState(null);
  const [openDetail, setOpenDetail] = useState(null);
  const load = () => fetch(`${API}/appointments/mine/`, { headers: authHeaders() }).then((r) => r.json()).then((d) => setAppts(d.appointments || []));
  useEffect(() => { load(); }, []); // eslint-disable-line
  const act = async (id, action) => { await fetch(`${API}/appointments/${id}/action/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ action }) }); load(); };
  if (!localStorage.getItem('token')) return <p style={{ fontSize: 13 }}>{t('cv_please_login')}</p>;
  if (appts === null) return <p style={{ fontSize: 13, color: C.muted }}>{t('loading')}</p>;
  if (appts.length === 0) return <p style={{ fontSize: 13, color: C.muted }}>{t('hl_no_appts')}</p>;
  return (
    <div>
      {appts.map((a) => (
        <div key={a.id + a.role} style={{ background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 12, padding: '1rem', marginBottom: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 14, fontWeight: 600 }}>{a.role === 'patient' ? `Dr. ${a.doctor}` : `@${a.patient}`}</span>
            <span style={{ fontSize: 12, fontWeight: 600, color: a.status === 'confirmed' ? C.primary : C.muted }}>{a.status}</span>
          </div>
          {a.reason && <p style={{ fontSize: 13, color: C.text, margin: '4px 0' }}>{a.reason}</p>}
          <p style={{ fontSize: 12, color: C.muted, margin: '2px 0' }}>{new Date(a.scheduled_datetime).toLocaleString()}</p>
          {(a.status === 'confirmed' || a.status === 'booked') && (
            <a href={`https://meet.jit.si/${a.room_code}`} target="_blank" rel="noreferrer" style={{ ...btn, display: 'inline-block', textDecoration: 'none', marginTop: 6 }}>🎥 {t('hl_join_call')}</a>
          )}
          <a href={`/chat/${a.role === 'patient' ? a.doctor_username : a.patient}`} style={{ ...btnGhost, display: 'inline-block', textDecoration: 'none', marginTop: 6, marginLeft: 6 }}>💬 {t('hl_chat')}</a>
          <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            {a.role === 'doctor' && a.status === 'booked' && <button style={{ ...btnGhost, fontSize: 12, padding: '5px 12px' }} onClick={() => act(a.id, 'confirm')}>{t('hl_confirm_appt')}</button>}
            {a.role === 'doctor' && a.status === 'confirmed' && <button style={{ ...btnGhost, fontSize: 12, padding: '5px 12px' }} onClick={() => act(a.id, 'complete')}>{t('hl_complete_appt')}</button>}
            {a.status !== 'completed' && a.status !== 'cancelled' && <button style={{ ...btnGhost, fontSize: 12, padding: '5px 12px', color: '#A32D2D' }} onClick={() => act(a.id, 'cancel')}>{t('hl_cancel_appt')}</button>}
          </div>
          <button style={{ ...btnGhost, fontSize: 12, padding: '5px 12px', marginTop: 6 }} onClick={() => setOpenDetail(openDetail === a.id ? null : a.id)}>📋 {t('hp_details')}</button>
          {openDetail === a.id && <ApptDetail appt={a} onClose={() => setOpenDetail(null)} />}
          {a.status === 'completed' && a.role === 'patient' && <ReviewBox module="doctor" objectId={a.id} objectTitle={a.doctor} />}
        </div>
      ))}
    </div>
  );
}

const FAC_LABEL = { lab: 'hf_labs', pharmacy: 'hf_pharmacy', hospital: 'hf_hospitals' };
const FAC_ICON = { lab: '🧪', pharmacy: '💊', hospital: '🏥' };

function Facilities({ type, setType }) {
  const [list, setList] = useState(null);
  const [city, setCity] = useState('');
  const [adding, setAdding] = useState(false);
  const [mine, setMine] = useState([]);
  const [form, setForm] = useState({ facility_type: 'lab', name: '', city: '', address: '', contact: '', description: '', home_service: false, emergency_24h: false });
  const [msg, setMsg] = useState('');

  const load = (ty, ct) => fetch(`${API}/facilities/?type=${ty}&city=${encodeURIComponent(ct || '')}`).then((r) => r.json()).then((d) => setList(d.facilities || []));
  const loadMine = () => { if (localStorage.getItem('token')) fetch(`${API}/facilities/mine/`, { headers: authHeaders() }).then((r) => r.json()).then((d) => setMine(d.facilities || [])); };
  useEffect(() => { load(type, city); loadMine(); }, [type]); // eslint-disable-line

  const submit = async () => {
    if (!form.name || !form.city || !form.contact) { setMsg('Name, city, contact zaroori'); return; }
    const res = await fetch(`${API}/facilities/mine/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(form) });
    const d = await safeJson(res);
    if (d.success) { setAdding(false); setForm({ facility_type: 'lab', name: '', city: '', address: '', contact: '', description: '', home_service: false, emergency_24h: false }); loadMine(); setMsg('✅'); }
    else setMsg(d.error || 'Failed');
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
        {Object.keys(FAC_LABEL).map((k) => (
          <button key={k} onClick={() => setType(k)}
            style={{ padding: '6px 12px', fontSize: 12.5, fontWeight: 600, border: `0.5px solid ${type === k ? C.primary : C.border}`, borderRadius: 20, background: type === k ? '#E1F5EE' : '#fff', color: type === k ? C.primary : C.text, cursor: 'pointer' }}>
            {FAC_ICON[k]} {t(FAC_LABEL[k])}
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        <input style={{ ...inp, marginBottom: 0, flex: 1 }} placeholder={t('hl_city')} value={city} onChange={(e) => setCity(e.target.value)} />
        <button style={btnGhost} onClick={() => load(type, city)}>{t('search')}</button>
      </div>

      {list === null && <p style={{ fontSize: 13, color: C.muted }}>{t('loading')}</p>}
      {list && list.length === 0 && <p style={{ fontSize: 13, color: C.muted }}>{t('hf_none')}</p>}
      {list && list.map((f) => (
        <div key={f.id} style={{ background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 10, padding: '0.85rem 1rem', marginBottom: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
            <div>
              <p style={{ fontSize: 14, fontWeight: 600, margin: '0 0 2px' }}>{FAC_ICON[f.type]} {f.name}</p>
              <p style={{ fontSize: 12, color: C.muted, margin: 0 }}>📍 {f.city}{f.address ? ` · ${f.address}` : ''}</p>
              {f.description && <p style={{ fontSize: 12, color: C.text, margin: '4px 0 0' }}>{f.description}</p>}
              <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                {f.home_service && <span style={{ fontSize: 11, color: C.primary, background: '#E1F5EE', padding: '1px 7px', borderRadius: 8 }}>🏠 {t('hf_home_service')}</span>}
                {f.emergency_24h && <span style={{ fontSize: 11, color: '#C0392B', background: '#FDECEA', padding: '1px 7px', borderRadius: 8 }}>🚨 {t('hf_emergency')}</span>}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
              <a href={`tel:${f.contact}`} style={{ ...btn, textDecoration: 'none', whiteSpace: 'nowrap' }}>📞</a>
              <a href={`https://wa.me/${f.contact.replace(/[^0-9]/g, '')}`} target="_blank" rel="noreferrer" style={{ ...btnGhost, textDecoration: 'none', whiteSpace: 'nowrap' }}>💬</a>
            </div>
          </div>
        </div>
      ))}

      <div style={{ borderTop: `0.5px solid ${C.border}`, marginTop: 16, paddingTop: 12 }}>
        {mine.length > 0 && (
          <div style={{ marginBottom: 10 }}>
            <p style={{ fontSize: 12, fontWeight: 600, margin: '0 0 4px' }}>{t('hf_my_facilities')}</p>
            {mine.map((f) => (
              <p key={f.id} style={{ fontSize: 12, color: C.muted, margin: '2px 0' }}>{FAC_ICON[f.type]} {f.name} · {f.city} {!f.is_approved && <span style={{ color: '#C0392B' }}>({t('hf_pending')})</span>}</p>
            ))}
          </div>
        )}
        {!adding && localStorage.getItem('token') && <button style={btnGhost} onClick={() => setAdding(true)}>➕ {t('hf_register')}</button>}
        {adding && (
          <div style={{ background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 12, padding: '1rem', marginTop: 8 }}>
            <label style={lbl}>{t('hf_type')}</label>
            <select style={inp} value={form.facility_type} onChange={set('facility_type')}>
              {Object.keys(FAC_LABEL).map((k) => <option key={k} value={k}>{t(FAC_LABEL[k])}</option>)}
            </select>
            <label style={lbl}>{t('hf_name')}</label><input style={inp} value={form.name} onChange={set('name')} />
            <label style={lbl}>{t('hl_city')}</label><input style={inp} value={form.city} onChange={set('city')} />
            <label style={lbl}>{t('hf_address')}</label><input style={inp} value={form.address} onChange={set('address')} />
            <label style={lbl}>{t('bb_contact')}</label><input style={inp} value={form.contact} onChange={set('contact')} />
            <label style={lbl}>{t('hf_services')}</label><textarea style={{ ...inp, minHeight: 50 }} value={form.description} onChange={set('description')} />
            <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
              <input type="checkbox" checked={form.home_service} onChange={(e) => setForm({ ...form, home_service: e.target.checked })} /> {t('hf_home_service')}
            </label>
            <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
              <input type="checkbox" checked={form.emergency_24h} onChange={(e) => setForm({ ...form, emergency_24h: e.target.checked })} /> {t('hf_emergency')}
            </label>
            <button style={btn} onClick={submit}>{t('hf_submit')}</button>{' '}
            <button style={btnGhost} onClick={() => setAdding(false)}>✕</button>
            {msg && <span style={{ fontSize: 12, color: C.primary, marginLeft: 8 }}>{msg}</span>}
          </div>
        )}
      </div>
      <p style={{ fontSize: 11, color: C.muted, marginTop: 12, fontStyle: 'italic' }}>{t('hf_disclaimer')}</p>
    </div>
  );
}

function BecomeDoctor() {
  const [form, setForm] = useState(null);
  const [msg, setMsg] = useState('');
  const [pending, setPending] = useState(false);
  useEffect(() => {
    fetch(`${API}/doctors/mine/`, { headers: authHeaders() }).then((r) => r.json()).then((d) => {
      if (d.exists) { setForm(d); setPending(!d.is_approved); }
      else setForm({ full_name: '', department: 'general', qualifications: '', clinic_name: '', city: '', bio: '', availability_note: '', consultation_fee_pi: 0, pi_wallet_address: '' });
    });
  }, []);
  if (!localStorage.getItem('token')) return <p style={{ fontSize: 13 }}>{t('cv_please_login')}</p>;
  if (!form) return <p style={{ fontSize: 13, color: C.muted }}>{t('loading')}</p>;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async () => {
    const res = await fetch(`${API}/doctors/mine/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(form) });
    const d = await safeJson(res);
    setMsg(d.success ? '✅ Saved' : (d.error || 'Failed'));
    if (d.success && !d.is_approved) setPending(true);
  };
  return (
    <div style={{ background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 12, padding: '1.2rem', maxWidth: 520 }}>
      {pending && <p style={{ fontSize: 12, color: '#C0392B', margin: '0 0 10px' }}>⏳ {t('hl_pending_approval')}</p>}
      <label style={lbl}>Full name</label><input style={inp} value={form.full_name} onChange={set('full_name')} />
      <label style={lbl}>{t('hl_department')}</label>
      <select style={inp} value={form.department} onChange={set('department')}>
        {Object.entries(DEPTS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select>
      <label style={lbl}>Qualifications</label><input style={inp} value={form.qualifications} onChange={set('qualifications')} placeholder="MBBS, FCPS" />
      <label style={lbl}>Clinic / Hospital</label><input style={inp} value={form.clinic_name} onChange={set('clinic_name')} />
      <label style={lbl}>{t('hl_city')}</label><input style={inp} value={form.city} onChange={set('city')} />
      <label style={lbl}>Availability</label><input style={inp} value={form.availability_note} onChange={set('availability_note')} placeholder="Mon-Fri 5-9pm" />
      <label style={lbl}>Bio</label><textarea style={{ ...inp, minHeight: 50 }} value={form.bio} onChange={set('bio')} />
      {msg && <p style={{ fontSize: 13, color: C.primary }}>{msg}</p>}
      <button style={btn} onClick={save}>Save profile</button>
    </div>
  );
}


function MyReports() {
  const [reports, setReports] = useState(null);
  const [file, setFile] = useState(null);
  const [reportType, setReportType] = useState('');
  const [concern, setConcern] = useState('');
  const [busy, setBusy] = useState(false);
  const [lastSuggestion, setLastSuggestion] = useState(null);

  const REPORT_TYPES = [
    ['xray_chest', 'Chest X-Ray'], ['xray_other', 'X-Ray (Other)'],
    ['blood', 'Blood Test'], ['ecg', 'ECG / Heart Test'],
    ['mri', 'MRI Scan'], ['ct', 'CT Scan'], ['ultrasound', 'Ultrasound'], ['other', 'Other'],
  ];

  const load = () => fetch(`${API}/reports/mine/`, { headers: authHeaders() }).then((r) => r.json()).then((d) => setReports(d.reports || []));
  useEffect(() => { load(); }, []);

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append('file', file);
    if (reportType) fd.append('report_type', reportType);
    if (concern) fd.append('concern', concern);
    const token = localStorage.getItem('token');
    const res = await fetch(`${API}/reports/upload/`, { method: 'POST', headers: { Authorization: `Token ${token}` }, body: fd });
    const data = await safeJson(res);
    setBusy(false);
    setFile(null); setReportType(''); setConcern('');
    if (data.suggested_department_label) setLastSuggestion(data.suggested_department_label);
    load();
  };

  const grouped = {};
  (reports || []).forEach((r) => { (grouped[r.date] = grouped[r.date] || []).push(r); });

  return (
    <div>
      <div style={{ background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 12, padding: '1rem', marginBottom: 14 }}>
        <p style={{ fontSize: 14, fontWeight: 600, margin: '0 0 8px' }}>{t('hl_upload_report')}</p>
        <input type="file" onChange={(e) => setFile(e.target.files[0])} style={{ marginBottom: 8, fontSize: 13 }} />
        <select style={{ width: '100%', padding: 8, fontSize: 13, border: `0.5px solid ${C.border}`, borderRadius: 8, marginBottom: 8 }} value={reportType} onChange={(e) => setReportType(e.target.value)}>
          <option value="">{t('hl_report_type')}</option>
          {REPORT_TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <input style={{ width: '100%', padding: 8, fontSize: 13, border: `0.5px solid ${C.border}`, borderRadius: 8, marginBottom: 8, boxSizing: 'border-box' }} placeholder={t('hl_concern_ph')} value={concern} onChange={(e) => setConcern(e.target.value)} />
        <button onClick={upload} disabled={!file || busy} style={{ padding: '8px 18px', fontSize: 13, fontWeight: 600, border: 'none', borderRadius: 8, background: C.primary, color: '#fff', cursor: 'pointer', opacity: (!file || busy) ? 0.6 : 1 }}>{busy ? '...' : t('hl_upload_btn')}</button>
        {lastSuggestion && (
          <div style={{ marginTop: 10, background: '#E1F5EE', borderRadius: 8, padding: '8px 10px' }}>
            <p style={{ fontSize: 13, fontWeight: 600, color: C.primary, margin: 0 }}>{'\ud83d\udca1'} {t('hl_suggested_dept')}: {lastSuggestion}</p>
            <p style={{ fontSize: 11, color: C.muted, margin: '4px 0 0' }}>{t('hl_suggestion_note')}</p>
          </div>
        )}
      </div>

      {reports === null ? (
        <p style={{ fontSize: 13, color: C.muted }}>{t('loading')}</p>
      ) : reports.length === 0 ? (
        <p style={{ fontSize: 13, color: C.muted }}>{t('hl_no_reports')}</p>
      ) : (
        Object.keys(grouped).sort().reverse().map((date) => (
          <div key={date} style={{ marginBottom: 12 }}>
            <p style={{ fontSize: 12, fontWeight: 700, color: C.muted, margin: '0 0 6px' }}>{date}</p>
            {grouped[date].map((r) => (
              <div key={r.id} style={{ background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 10, padding: '0.7rem 1rem', marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <p style={{ fontSize: 13, fontWeight: 600, margin: 0 }}>{r.title}</p>
                  {r.concern && <p style={{ fontSize: 12, color: C.text, margin: '3px 0 0', fontStyle: 'italic' }}>{r.concern}</p>}
                  <p style={{ fontSize: 11, color: C.muted, margin: '2px 0 0' }}>
                    {r.doctor ? `Dr. ${r.doctor}` : 'Standalone'}
                    {r.suggested_department_label && ` · \ud83d\udca1 ${r.suggested_department_label}`}
                  </p>
                </div>
                <a href={r.url} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: C.primary, fontWeight: 600, textDecoration: 'none' }}>{t('hl_view')}</a>
              </div>
            ))}
          </div>
        ))
      )}
    </div>
  );
}

function HealthCart() {
  const [items, setItems] = useState(null);
  const [paying, setPaying] = useState(false);
  const [msg, setMsg] = useState('');

  const load = () => fetch('/api/cart/', { headers: authHeaders() }).then((r) => r.json()).then((d) => {
    setItems((d.items || []).filter((i) => i.app_name === 'health'));
  });
  useEffect(() => { load(); }, []);

  const removeItem = async (id) => {
    await fetch(`/api/cart/${id}/remove/`, { method: 'DELETE', headers: authHeaders() });
    load();
  };

  const payOne = (planId, pricePi, planName) => new Promise((resolve, reject) => {
    if (!window.Pi) { reject(new Error('Pi Browser mein kholein')); return; }
    window.Pi.createPayment(
      { amount: pricePi, memo: 'Health subscription', metadata: { type: 'health_plan', plan_id: planId } },
      {
        onReadyForServerApproval: (paymentId) => {
          fetch('/api/payments/approve/', { method: 'POST', headers: authHeaders(), body: JSON.stringify({ paymentId, amount: pricePi }) });
        },
        onReadyForServerCompletion: (paymentId, txid) => {
          fetch('/api/payments/complete/', { method: 'POST', headers: authHeaders(), body: JSON.stringify({ paymentId, txid }) })
            .then(() => fetch(`${API}/subscribe/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ plan_id: planId, payment_id: paymentId, txid }) }))
            .then(() => resolve())
            .catch(() => reject(new Error('Payment completed but subscription failed for ' + planName + ' - contact support.')));
        },
        onCancel: () => reject(new Error('Cancelled')),
        onError: () => reject(new Error('Payment error')),
      }
    );
  });

  const checkoutAll = async () => {
    setPaying(true); setMsg('');
    try {
      await window.Pi.authenticate(['username', 'payments'], () => {});
      const res = await fetch(`${API}/checkout-cart/`, { headers: authHeaders() });
      const data = await safeJson(res);
      for (const it of (data.items || [])) {
        await payOne(it.plan_id, it.price_pi, it.plan_name);
        const cartItem = items.find((x) => x.reference_id === String(it.plan_id));
        if (cartItem) await fetch(`/api/cart/${cartItem.id}/remove/`, { method: 'DELETE', headers: authHeaders() });
      }
      alert('✅ ' + t('hl_all_subscribed'));
      window.location.reload();
    } catch (e) {
      setMsg(e.message);
    }
    setPaying(false);
  };

  if (items === null) return <p style={{ fontSize: 13, color: C.muted }}>{t('loading')}</p>;
  if (items.length === 0) return <p style={{ fontSize: 13, color: C.muted }}>{t('hl_cart_empty')}</p>;

  const total = items.reduce((s, i) => s + i.price_pi * i.quantity, 0);

  return (
    <div>
      {items.map((i) => (
        <div key={i.id} style={{ background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 10, padding: '0.8rem 1rem', marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <p style={{ fontSize: 14, fontWeight: 600, margin: 0 }}>{i.title}</p>
            <p style={{ fontSize: 12, color: C.primary, margin: '2px 0 0' }}><PiPrice pi={i.price_pi} /></p>
          </div>
          <button onClick={() => removeItem(i.id)} style={{ fontSize: 12, color: '#A32D2B', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
        </div>
      ))}
      <p style={{ fontSize: 15, fontWeight: 700, margin: '10px 0' }}>{t('hl_total')}: {total} π</p>
      {msg && <p style={{ fontSize: 13, color: '#A32D2D', marginBottom: 8 }}>{msg}</p>}
      <button onClick={checkoutAll} disabled={paying} style={{ ...btn, opacity: paying ? 0.6 : 1 }}>{paying ? '...' : t('hl_pay_subscribe')}</button>
    </div>
  );
}

function DoctorTips() {
  const [tips, setTips] = useState(null);
  const [myTips, setMyTips] = useState(null);
  const [form, setForm] = useState({ title: '', video_url: '', description: '' });
  const [msg, setMsg] = useState('');
  const [showForm, setShowForm] = useState(false);

  const load = () => fetch(`${API}/tips/`).then((r) => r.json()).then((d) => setTips(d.tips || []));
  const loadMine = () => {
    if (!localStorage.getItem('token')) return;
    fetch(`${API}/tips/mine/`, { headers: authHeaders() }).then((r) => {
      if (r.status === 403) { setMyTips(false); return null; }
      return r.json();
    }).then((d) => { if (d) setMyTips(d.tips || []); });
  };
  useEffect(() => { load(); loadMine(); }, []);

  const post = async () => {
    if (!form.title || !form.video_url) { setMsg('Title and link required'); return; }
    const res = await fetch(`${API}/tips/mine/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(form) });
    const d = await safeJson(res);
    if (d.success) { setForm({ title: '', video_url: '', description: '' }); setMsg('✅'); load(); loadMine(); }
    else setMsg(d.error || 'Failed');
  };
  const removeTip = async (id) => {
    await fetch(`${API}/tips/${id}/delete/`, { method: 'DELETE', headers: authHeaders() });
    load(); loadMine();
  };

  return (
    <div>
      {myTips !== false && myTips !== null && (
        <div style={{ background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 12, padding: '1rem', marginBottom: 16 }}>
          {!showForm ? (
            <button style={btnGhost} onClick={() => setShowForm(true)}>➕ {t('hl_add_tip')}</button>
          ) : (
            <>
              <input style={inp} placeholder={t('hl_tip_title_ph')} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              <input style={inp} placeholder={t('hl_tip_url_ph')} value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} />
              <textarea style={{ ...inp, minHeight: 50 }} placeholder={t('hl_tip_desc_ph')} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              <button style={btn} onClick={post}>{t('hl_tip_post')}</button>{' '}
              <button style={btnGhost} onClick={() => setShowForm(false)}>✕</button>
              {msg && <p style={{ fontSize: 12, color: C.primary, marginTop: 6 }}>{msg}</p>}
            </>
          )}
          {myTips.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <p style={{ fontSize: 12, fontWeight: 600, margin: '0 0 4px' }}>{t('hl_my_tips')}</p>
              {myTips.map((t2) => (
                <p key={t2.id} style={{ fontSize: 12, color: C.muted, margin: '2px 0' }}>
                  {t2.title} <button onClick={() => removeTip(t2.id)} style={{ fontSize: 11, color: '#A32D2D', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
                </p>
              ))}
            </div>
          )}
        </div>
      )}

      <p style={{ fontSize: 15, fontWeight: 700, margin: '0 0 10px' }}>{t('hl_tips_title')}</p>
      {tips === null && <p style={{ fontSize: 13, color: C.muted }}>{t('loading')}</p>}
      {tips && tips.length === 0 && <p style={{ fontSize: 13, color: C.muted }}>{t('hl_tips_none')}</p>}
      {tips && tips.map((tp) => (
        <a key={tp.id} href={tp.video_url} target="_blank" rel="noreferrer" style={{ display: 'block', textDecoration: 'none', color: 'inherit', background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 12, overflow: 'hidden', marginBottom: 12 }}>
          {tp.thumbnail && (
            <div style={{ position: 'relative' }}>
              <img src={tp.thumbnail} alt={tp.title} style={{ width: '100%', display: 'block' }} />
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, color: '#fff' }}>▶</div>
              </div>
            </div>
          )}
          <div style={{ padding: '0.75rem 1rem' }}>
            <p style={{ fontSize: 14, fontWeight: 600, margin: '0 0 2px' }}>{tp.title}</p>
            <p style={{ fontSize: 12, color: C.primary, margin: 0 }}>Dr. {tp.doctor_name} · {DEPTS[tp.department] || tp.department}</p>
            {tp.description && <p style={{ fontSize: 12, color: C.muted, margin: '4px 0 0' }}>{tp.description}</p>}
          </div>
        </a>
      ))}
    </div>
  );
}